// En un correo (Bandeja -> Correos): si ya es caso, el enlace a ese caso; si no, "Convertir en caso" o "Agregar a un
// caso" (como en Zammad, para correos que no abrieron caso solos, p. ej. los del buzon de una persona).
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { agregarCorreoACaso, aCaso, buscarCasos, type Caso, casoDesdeCorreo, type RawCaso } from '../../api/casos';
import { gqlTodos } from '../../api/gql';
import { CAMPOS_LISTA } from '../../api/vistasCaso';
import { useSession } from '../../auth/session';
import { useMiembros } from '../../data/queries';
import { Icon } from '../../lib/icons';
import { OpcionCaso } from '../../ui/OpcionCaso';
import { Dialog } from '../../ui/Dialog';
import { toast } from '../../ui/toasts';
import { refrescarCasos, useMisGrupos } from './useVistas';

export function CasoDeCorreo({ hiloId, asunto, correoCliente, personaId }: { hiloId: string; asunto: string; correoCliente: string; personaId: string | null }) {
  const r = useQuery({
    queryKey: ['caso-de-hilo', hiloId],
    queryFn: async () =>
      (await gqlTodos<RawCaso>('casos', CAMPOS_LISTA, { filtro: { or: [{ hiloId: { eq: hiloId } }, { hilosUnidos: { containsIlike: `%${hiloId}%` } }] }, max: 1 })).map(aCaso)[0] ?? null,
    staleTime: 30_000,
  });
  const [abierto, setAbierto] = useState(false);
  if (r.isLoading) return null;
  if (r.data)
    return (
      <Link className="btn sm ghost" to={`/bandeja/casos?caso=${r.data.id}`} title="Ver el caso de este correo">
        <Icon name="inbox" /> <span className="lbl-m">Caso #{r.data.numero}</span>
      </Link>
    );
  return (
    <>
      <button className="btn sm ghost" onClick={() => setAbierto(true)} title="Convertir este correo en caso o agregarlo a uno">
        <Icon name="inbox" /> <span className="lbl-m">A caso</span>
      </button>
      {abierto ? <ConvertirDialog hiloId={hiloId} asunto={asunto} correoCliente={correoCliente} personaId={personaId} onClose={() => setAbierto(false)} /> : null}
    </>
  );
}

function ConvertirDialog({ hiloId, asunto, correoCliente, personaId, onClose }: { hiloId: string; asunto: string; correoCliente: string; personaId: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const { yo } = useSession();
  const miembros = useMiembros();
  const grupos = useMisGrupos();
  const [modo, setModo] = useState<'nuevo' | 'existente'>('nuevo');
  const [v, setV] = useState({ grupo: grupos.nombres[0] ?? '', asignadoId: yo.miembroId ?? '' });
  const [q, setQ] = useState('');
  const [destino, setDestino] = useState<Caso | null>(null);
  const [guardando, setGuardando] = useState(false);
  const buscar = useQuery({ queryKey: ['buscar-casos', q], queryFn: () => buscarCasos(q), enabled: modo === 'existente' && q.trim().length >= 2 });
  const guardar = async () => {
    setGuardando(true);
    try {
      if (modo === 'nuevo') {
        const c = await casoDesdeCorreo({ hiloId, asunto, correoCliente, grupo: v.grupo, asignadoId: v.asignadoId || null, personaId });
        toast(`Caso #${c.numero} creado.`);
      } else if (destino) {
        await agregarCorreoACaso(destino, hiloId, yo.nombre);
        toast(`Correo agregado al caso #${destino.numero}.`);
      }
      refrescarCasos(qc);
      onClose();
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setGuardando(false);
    }
  };
  return (
    <Dialog titulo="Llevar este correo a un caso" subtitulo={asunto} onClose={onClose} width={560}>
      <div className="form">
        <div className="seg" role="group" aria-label="Qué hacer">
          <button type="button" aria-pressed={modo === 'nuevo'} onClick={() => setModo('nuevo')}>
            Caso nuevo
          </button>
          <button type="button" aria-pressed={modo === 'existente'} onClick={() => setModo('existente')}>
            Agregar a un caso existente
          </button>
        </div>
        {modo === 'nuevo' ? (
          <div className="grid2">
            <label>
              Grupo
              <select id="cc-grupo" value={v.grupo} onChange={(e) => setV({ ...v, grupo: e.target.value })}>
                {grupos.nombres.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Asignado
              <select id="cc-asignado" value={v.asignadoId} onChange={(e) => setV({ ...v, asignadoId: e.target.value })}>
                <option value="">Sin asignar</option>
                {(miembros.data ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nombre}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : (
          <>
            <label>
              Buscar el caso (número, asunto o correo)
              <input id="cc-q" className="inp" value={q} onChange={(e) => (setQ(e.target.value), setDestino(null))} placeholder="#20260810620019 o Ariza" autoFocus />
            </label>
            <div className="unir-res">
              {(buscar.data ?? []).map((x) => (
                <OpcionCaso key={x.id} caso={x} elegido={destino?.id === x.id} onElegir={() => setDestino(x)} />
              ))}
            </div>
          </>
        )}
        <div className="actions">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn primary" disabled={guardando || (modo === 'nuevo' ? !v.grupo : !destino)} onClick={guardar}>
            {guardando ? 'Guardando…' : modo === 'nuevo' ? 'Crear caso' : destino ? `Agregar al #${destino.numero}` : 'Elige un caso'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
